import {
  WebGLRenderer,
  Clock,
  ACESFilmicToneMapping,
} from 'three';
import type { IScene, PostProcessConfig } from './types';
import { AssetLoader } from './AssetLoader';
import { PostProcessing } from './PostProcessing';

export class Engine {
  readonly renderer: WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly loader: AssetLoader;
  private clock: Clock;
  private activeScene: IScene | null = null;
  private postProcessing: PostProcessing | null = null;
  private animationId: number | null = null;
  private postProcessConfig: PostProcessConfig | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.loader = new AssetLoader();
    this.clock = new Clock();

    this.renderer = new WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
      stencil: false,
    });

    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.setClearColor(0x000000, 1);

    window.addEventListener('resize', this.onResize);
    canvas.addEventListener('webglcontextlost', this.onContextLost);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored);
  }

  setPostProcessConfig(config: PostProcessConfig): void {
    this.postProcessConfig = config;
    if (config.toneMapping?.exposure !== undefined) {
      this.renderer.toneMappingExposure = config.toneMapping.exposure;
    }
  }

  async loadScene(scene: IScene): Promise<void> {
    this.activeScene?.dispose();
    this.postProcessing?.dispose();
    this.postProcessing = null;

    await scene.load(this.loader);
    await scene.setup({ renderer: this.renderer, canvas: this.canvas });

    if (this.postProcessConfig) {
      this.postProcessing = new PostProcessing(
        this.renderer,
        scene.getScene(),
        scene.getCamera(),
        this.postProcessConfig,
      );
    }

    this.activeScene = scene;
  }

  start(): void {
    if (this.animationId !== null) return;
    this.clock.start();
    this.tick();
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  dispose(): void {
    this.stop();
    this.activeScene?.dispose();
    this.postProcessing?.dispose();
    this.loader.dispose();
    this.renderer.dispose();
    window.removeEventListener('resize', this.onResize);
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
  }

  private tick = (): void => {
    this.animationId = requestAnimationFrame(this.tick);
    const delta = this.clock.getDelta();
    const elapsed = this.clock.getElapsedTime();

    this.activeScene?.update(delta, elapsed);

    if (this.postProcessing) {
      this.postProcessing.render();
    } else if (this.activeScene) {
      this.renderer.render(this.activeScene.getScene(), this.activeScene.getCamera());
    }
  };

  private onResize = (): void => {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    const pixelRatio = Math.min(window.devicePixelRatio, 2);

    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(pixelRatio);
    this.postProcessing?.resize(width, height, pixelRatio);

    if (this.activeScene) {
      const cam = this.activeScene.getCamera();
      if ('aspect' in cam) {
        (cam as { aspect: number }).aspect = width / height;
      }
      if ('updateProjectionMatrix' in cam) {
        (cam as { updateProjectionMatrix: () => void }).updateProjectionMatrix();
      }
    }
  };

  private onContextLost = (e: Event): void => {
    e.preventDefault();
    this.stop();
  };

  private onContextRestored = (): void => {
    this.start();
  };
}
